<template>
  <div class="flex flex-col gap-4 max-h-[400px] pt-4">
    <div class="flex flex-col gap-4 px-2">
      <div class="flex items-center">
        <tx-checkbox
          v-model="allSelected"
          :label="t('personalSettings.settings.summaryCharts')"
          :indeterminate="isIndeterminate"
          class="cursor-pointer"
          @change="handleChange"
        />
      </div>

      <div class="relative w-full">
        <tx-input
          v-model="filterText"
          :placeholder="t('general.filter')"
          clearable
        />
      </div>
    </div>

    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-4 gap-x-4 px-2">
      <div
        v-for="chart in filteredCharts"
        :key="chart.SystemName"
        class="flex items-center"
      >
        <tx-checkbox
          v-model="selectedCharts[chart.SystemName]"
          :label="chart.DisplayName"
          class="cursor-pointer"
          @change="handleChange"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import TxInput from '@/shared/components/TxInput.vue'
import TxCheckbox from '@/shared/components/TxCheckbox.vue'

interface IFormEditorAttribute {
  SystemName: string
  DisplayName: string
}

const props = defineProps({
  charts: {
    type: Array as () => IFormEditorAttribute[],
    required: true,
    default: () => [],
  },
  modelValue: {
    type: Array as () => string[],
    required: true,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue', 'change'])

const { t } = useI18n()
const filterText = ref('')

const selectedCharts = ref<Record<string, boolean>>({})

function initializeSelection() {
  props.charts.forEach((chart) => {
    selectedCharts.value[chart.SystemName] = props.modelValue.includes(chart.SystemName)
  })
}

initializeSelection()

watch(() => props.modelValue, () => {
  initializeSelection()
})

function handleChange() {
  const selected = Object.keys(selectedCharts.value)
    .filter(key => selectedCharts.value[key])
  emit('update:modelValue', selected)
}

const allSelected = computed({
  get: () => props.charts.length > 0 && props.charts.every(chart => selectedCharts.value[chart.SystemName]),
  set: (value: boolean) => {
    props.charts.forEach((chart) => {
      selectedCharts.value[chart.SystemName] = value
    })
    handleChange()
  },
})

const isIndeterminate = computed(() => {
  const selectedCount = Object.values(selectedCharts.value).filter(Boolean).length
  return selectedCount > 0 && selectedCount < props.charts.length
})

const filteredCharts = computed(() => {
  if (!filterText.value.trim()) { return props.charts }
  const searchTerm = filterText.value.toLowerCase()
  return props.charts.filter(chart =>
    chart.DisplayName.toLowerCase().includes(searchTerm),
  )
})
</script>
